/* eslint-disable react-hooks/exhaustive-deps */
import { Button } from "primereact/button";
import { useEffect, useRef, useState } from "react";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { InputText } from "primereact/inputtext";
import { SORT_FILTER, STATUS_FILTER } from "../../../../util/constants";
import { ProgressSpinner } from "primereact/progressspinner";
import DataTableComponent, { ColumnOption } from "../../../common/DataTable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { getErrorMessage } from "../../../../helper/getResponseMessage";
import { showError, showSuccess } from "../../../../util/ShowStatus";
import axios from "../../../../util/axiosconfig";
import { useDebounce } from "../../../../util/debounce";
import { fetchVehicleTypes } from "./fetchData";
import { tableHeaderTemplate } from "../common/TimeLogTemplates";
import FilterButton from "../common/FilterButton";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { EditButtonSVG } from "../../../../assets/SVGs/EditButtonSVG";
import { Timeline } from "primereact/timeline";
import { Card } from "primereact/card";
import { convertEpochtoDate } from "../../../../helper/DateConverters";

export interface Vehicle {
  vehicleID?: number;
  registration_no: string | null;
  vehicleType: any;
  make?: string | null;
  model?: string | null;
  capacity: string | null;
  isActive: boolean;
  vehicleStatusChangeHistory?: any[];
}

interface FormErrors {
  registration_no: string | null;
  vehicleType: string | null;
  make?: string | null;
  model?: string | null;
  capacity: string | null;
  isActive: string | null;
}

interface Filters {
  registration_no: string | null;
  vehicleType: any;
  capacity: string | null;
  status: "all" | "active" | "inactive";
}

interface VehicleType {
  vehicleTypeID: number;
  vehicleType: string;
}

const VehicleManagement = () => {
  const [vehiclesLoading, setVehiclesLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string | null>("all");
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false);
  const [formType, setFormType] = useState<string>("add");
  const [isAddButtonDisabled, setIsAddButtonDisabled] =
    useState<boolean>(false);

  const [formValues, setFormValues] = useState<Vehicle>({
    registration_no: null,
    vehicleType: null,
    make: null,
    model: null,
    capacity: null,
    isActive: true,
  });

  const [filters, setFilters] = useState<Filters>({
    registration_no: null,
    vehicleType: null,
    capacity: null,
    status: "all",
  });

  const searchCapacityWithDebounce = useDebounce(
    filters?.capacity?.trim() ?? null
  );

  const [formErrors, setFormErrors] = useState<FormErrors>({
    registration_no: null,
    vehicleType: null,
    make: null,
    model: null,
    capacity: null,
    isActive: null,
  });

  const [sortFilter, setSortFilter] = useState<any>({
    sortField: "createdDate",
    sortOrder: false,
  });
  const [isStatusDialogVisible, setIsStatusDialogVisible] =
    useState<boolean>(false);
  const [statusChangeReason, setStatusChangeReason] = useState<string>("");
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    vehicleID: number;
    isActive: boolean;
  } | null>(null);
  const [statusChangeReasonError, setStatusChangeReasonError] = useState<
    string | null
  >(null);
  const [viewHistoryPopup, setViewHistoryPopup] = useState<boolean>(false);
  const [vehicleStatusChangeActivity, setVehicleStatusChangeActivity] =
    useState<any>([]);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const searchVehicleRegistrationWithDebounce = useDebounce(
    filters?.registration_no?.trim() ?? null
  );
  const [first, setFirst] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(10); // To hold the total number of records
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [vehicles, setVehicles] = useState<Vehicle[]>([]); // To hold the list of vehicles

  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const page = 0;

  const menu = useRef<Menu>(null);
  const toast = useRef<Toast>(null);

  const fetchVehicles = async (page: number, size: number, type?: string) => {
    try {
      const params: any = {
        size,
        page,
        sortField: sortFilter.sortField,
        sortOrder:
          sortFilter.sortOrder === true ? SORT_FILTER.ASC : SORT_FILTER.DESC,
      };
      if (searchVehicleRegistrationWithDebounce) {
        params.registration_no = searchVehicleRegistrationWithDebounce;
      }

      if (filters?.vehicleType) {
        params.vehicleTypeID = filters.vehicleType?.vehicleTypeID;
      }

      if (searchCapacityWithDebounce) {
        params.capacity = searchCapacityWithDebounce;
      }
      if (statusFilter !== "all") {
        params.isActive = statusFilter === "active" ? true : false;
      }
      // setVehiclesLoading(true);
      // const response = await axios.get("tl-admin/vehicle/all", { params });
      // setVehicles(response.data.result);
      // setTotalRecords(response.data.totalRecords); // Assuming the API returns the total count
      if (type === "export") {
        try {
          setIsExporting(true);
          const response = await axios.get("tl-admin/export-vehicle", {
            params,
            responseType: "blob", // Important for binary data
          });

          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.download = "vehicle_report.csv";

          // Append to body and trigger download
          document.body.appendChild(link);
          link.click();

          // Clean up
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {}
      } else {
        setVehiclesLoading(true);
        const response = await axios.get("tl-admin/vehicle/all", { params });
        setVehicles(response.data.result);
        setTotalRecords(response.data.totalRecords); // Assuming the API returns the total count
      }
      setIsExporting(false);
      setVehiclesLoading(false);
    } catch (err) {
      const error = getErrorMessage(err);
      setVehiclesLoading(false);
      showError(error, toast);
    }
  };

  useEffect(() => {
    fetchVehicleTypes(setVehicleTypes, toast);
  }, []);

  useEffect(() => {
    setFirst(page);
    fetchVehicles(page, size);
  }, [
    searchVehicleRegistrationWithDebounce,
    filters?.vehicleType,
    searchCapacityWithDebounce,
    statusFilter,
    sortFilter,
  ]);

  const validateForm = () => {
    const errors: Partial<Record<string, string>> = {};

    if (
      !formValues.registration_no ||
      formValues.registration_no.trim() === ""
    ) {
      errors.registration_no = "Vehicle Registration is required";
    }
    if (!formValues.vehicleType) {
      errors.vehicleType = "Vehicle Type is required";
    }
    if (!formValues.capacity || formValues.capacity.trim() === "") {
      errors.capacity = "Capacity is required";
    }
    setFormErrors((prev) => ({ ...prev, ...errors }));

    return Object.keys(errors).length === 0; // Returns true if no errors
  };

  const handleAddUpdateVehicle = async (formType?: string) => {
    if (!validateForm()) return; // Stop submission if validation fails
    try {
      const vehicleData: any = {};
      setIsAddButtonDisabled(true);
      if (formType === "add") {
        vehicleData.registration_no = formValues?.registration_no?.trim();
        vehicleData.vehicleTypeID = vehicleTypes.find(
          (vt) => vt.vehicleType === formValues.vehicleType.vehicleType
        )?.vehicleTypeID;
        vehicleData.make = formValues?.make?.trim() || null;
        vehicleData.model = formValues?.model?.trim() || null;
        vehicleData.capacity = formValues?.capacity?.trim();
      } else {
        vehicleData.vehicleID = formValues?.vehicleID;
        vehicleData.registration_no = formValues?.registration_no?.trim();
        vehicleData.vehicleTypeID = formValues.vehicleType.vehicleTypeID;
        vehicleData.make = formValues?.make?.trim() || null;
        vehicleData.model = formValues?.model?.trim() || null;
        vehicleData.capacity = formValues?.capacity?.trim();
      }

      formType === "add"
        ? await axios.post("tl-admin/add-vehicle", vehicleData)
        : await axios.post("tl-admin/edit-vehicle", vehicleData);
      setIsDialogVisible(false);
      setFormValues({
        registration_no: null,
        vehicleType: null,
        capacity: null,
        isActive: formType === "add" ? true : formValues.isActive,
      });
      setFirst(page);
      fetchVehicles(page, size);
      setIsAddButtonDisabled(false);
    } catch (err) {
      const error = getErrorMessage(err);
      showError(error, toast);
      setIsAddButtonDisabled(false);
    }
  };

  const handleEditVehicle = (rowData: Vehicle) => {
    setFormType("update");
    const selectedVehicleType: any =
      vehicleTypes.find((vt) => vt.vehicleType === rowData.vehicleType) || null;
    setFormValues({
      vehicleID: rowData.vehicleID,
      registration_no: rowData.registration_no,
      vehicleType: selectedVehicleType,
      make: rowData.make,
      model: rowData.model,
      capacity: rowData.capacity,
      isActive: rowData.isActive,
    });
    setIsDialogVisible(true);
  };

  const updateVehicleStatus = async (
    vehicleID: any,
    isActive: boolean,
    reason: string
  ) => {
    try {
      setIsAddButtonDisabled(true);
      setFormValues((prevFormValues) => ({
        ...prevFormValues,
        vehicleID,
      }));
      const statusResponse = await axios.post("tl-admin/edit-vehicle", {
        vehicleID,
        isActive,
        statusChangeReason: reason,
      });
      if (statusResponse.status === 200) {
        showSuccess(statusResponse.data.Message, toast);
        const updatedVehicles = vehicles.map((vehicle) =>
          vehicle.vehicleID === vehicleID
            ? { ...vehicle, isActive: isActive }
            : vehicle
        );
        setVehicles(updatedVehicles);
      } else {
        setIsAddButtonDisabled(false);
      }
      setIsAddButtonDisabled(false);
    } catch (err) {
      const error = getErrorMessage(err);
      showError(error, toast);
      setIsAddButtonDisabled(false);
    }
  };
  const handleStatusChangeClick = (
    vehicleID: number,
    currentStatus: boolean
  ) => {
    setPendingStatusChange({
      vehicleID,
      isActive: !currentStatus,
    });
    setStatusChangeReason("");
    setIsStatusDialogVisible(true);
  };

  const confirmStatusChange = () => {
    if (!statusChangeReason.trim()) {
      setStatusChangeReasonError(
        "Please provide a reason for the status change"
      );
      return;
    }
    setStatusChangeReasonError(null);
    if (pendingStatusChange) {
      updateVehicleStatus(
        pendingStatusChange.vehicleID,
        pendingStatusChange.isActive,
        statusChangeReason
      ).then(() => {
        fetchVehicles(page, size);
        setIsStatusDialogVisible(false);
      });
    }
  };

  const formatCreatedDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getUTCDate().toString().padStart(2, "0");
    const month = date.toLocaleString("en-UK", { month: "short" });
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours().toString().padStart(2, "0");
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  const events: any = vehicleStatusChangeActivity?.map((activity: any) => ({
    actionTakerID: activity?.actionTakerID,
    date: activity?.createdDate,
    actionTakerDisplayName: activity?.actionTakerName,
    comment: activity?.statusChangeReason,
    action:
      activity?.previousStatus && activity?.newStatus
        ? `Status changed from ${
            activity.previousStatus.charAt(0).toUpperCase() +
            activity.previousStatus.slice(1).toLowerCase()
          } to ${
            activity.newStatus.charAt(0).toUpperCase() +
            activity.newStatus.slice(1).toLowerCase()
          }`
        : null,
    id: activity?.id,
    previousStatus: activity?.previousStatus,
    newStatus: activity?.newStatus,
  }));

  const customizedMarker = () => (
    <div className="tw-flex tw-justify-center tw-items-center tw-w-5 tw-h-5 tw-rounded-full tw-bg-[#E2E8F0]">
      <div className="tw-w-3 tw-h-3 tw-rounded-full tw-bg-[#00AFEE]"></div>
    </div>
  );

  const customizedContent = (item: any) =>
    item?.action ? (
      <Card className="activity-card tw-bg-gray-50">
        <div className="activity-container tw-flex tw-justify-between tw-items-center tw-mb-4">
          <span className="activity-date tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
            {formatCreatedDate(item?.date)}
          </span>
        </div>
        <span className="activity-content tw-text-ellipsis tw-overflow-hidden tw-max-w-52">
          <span>
            <span className="tw-font-medium tw-text-gray-700">
              {item?.action}
            </span>{" "}
            <span className="tw-text-gray-600">
              by {item?.actionTakerDisplayName}
            </span>
          </span>
        </span>
        <div className="tw-flex tw-justify-between tw-items-center tw-mt-1 tw-font-medium tw-text-[#6611119c]">
          <span className="tw-ml-auto">{item?.comment}</span>
        </div>
      </Card>
    ) : (
      <div>No History Found</div>
    );

  const onPageChange = (event: PaginatorPageChangeEvent) => {
    setFirst(event.first);
    setSize(event.rows); // Set the number of rows per page
    const currentPage = Math.floor(event.first / event.rows); // Calculate the current page index
    fetchVehicles(currentPage, event.rows);
  };

  const actionBodyTemplate = (rowData: Vehicle) => (
    <div className="tw-flex tw-gap-4">
      <Button
        className="p-button-rounded tw-p-0 tw-w-auto tw-h-auto"
        onClick={() => handleEditVehicle(rowData)}
      >
        <EditButtonSVG />
      </Button>
      <InputSwitch
        checked={rowData.isActive}
        onChange={(e: InputSwitchChangeEvent) => {
          handleStatusChangeClick(rowData.vehicleID!, rowData.isActive);
        }}
        disabled={
          isAddButtonDisabled && rowData.vehicleID === formValues.vehicleID
        }
      />
      <Button
        className="p-button-rounded tw-p-0 tw-w-auto tw-h-auto focus:tw-shadow-none"
        onClick={() => {
          setVehicleStatusChangeActivity(
            rowData?.vehicleStatusChangeHistory || []
          );
          setSelectedRowData(rowData);
          setViewHistoryPopup(true);
        }}
        tooltip="View Status History"
        tooltipOptions={{ position: "top" }}
      >
        <i className="pi pi-info-circle tw-text-[#00AFEE] tw-text-xl"></i>
      </Button>
    </div>
  );

  const vehicleColumns: ColumnOption[] = [
    {
      field: "registration_no",
      header: tableHeaderTemplate(
        "Vehicle Registration",
        "registration_no",
        setSortFilter
      ),
      visible: true,
    },
    {
      field: "vehicleType",
      header: tableHeaderTemplate("Vehicle Type", "type", setSortFilter),
      visible: true,
    },
    {
      field: "make",
      header: tableHeaderTemplate("Vehicle Make", "make", setSortFilter),
      visible: true,
      body: (e: any) => e.make || "-", // Display 'N/A' if make is null
    },
    {
      field: "model",
      header: tableHeaderTemplate("Vehicle Model", "model", setSortFilter),
      visible: true,
      body: (e: any) => e.model || "-", // Display 'N/A' if make is null
    },
    {
      field: "capacity",
      header: tableHeaderTemplate(
        "Capacity (in tons)",
        "capacity",
        setSortFilter
      ),
      // body: capacityTemplate,
      visible: true,
    },
    {
      field: "action",
      header: tableHeaderTemplate("Action", "", setSortFilter),
      body: actionBodyTemplate,
      visible: true,
    },
  ];

  const clearFilters = () => {
    setStatusFilter("all");
    setFilters({
      registration_no: null,
      vehicleType: null,
      capacity: null,
      status: "all",
    });
  };

  const items = [
    {
      items: [
        {
          template: (
            <div>
              <label htmlFor="capacity" className="tw-ml-5 tw-font-semibold">
                Vehicle Registration
              </label>
              <div className="tw-flex tw-gap-2 tw-items-center tw-mx-5 tw-w-62 tw-mt-2">
                <InputText
                  type="text"
                  className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-w-full tw-h-11"
                  value={filters?.registration_no ?? ""}
                  placeholder="Enter Vehicle Registration"
                  onChange={(e) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      registration_no: e.target.value,
                    }));
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-3">
              <label htmlFor="UploadedOn" className="tw-font-semibold">
                Vehicle Type
              </label>
              <Dropdown
                className="tw-h-11 tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-w-full placeholder:tw-font-normal filter-dropdown"
                value={filters?.vehicleType ?? null}
                options={vehicleTypes}
                optionLabel="vehicleType" // Assuming 'roles' contains an array of objects with a 'role' key
                placeholder="Select Vehicle Type"
                onChange={(e) => {
                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    vehicleType: e.target.value,
                  }));
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ),
        },
        {
          template: (
            <div>
              <label htmlFor="capacity" className="tw-ml-5 tw-font-semibold">
                Capacity
              </label>
              <div className="tw-flex tw-gap-2 tw-items-center tw-mx-5 tw-w-62 tw-mt-3">
                <InputText
                  type="text"
                  className="tw-pl-2 tw-border tw-border-gray-300 tw-rounded-md tw-w-full tw-h-11"
                  value={filters?.capacity ?? ""}
                  placeholder="Enter Vehicle Capacity"
                  onChange={(e) => {
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      capacity: e.target.value,
                    }));
                  }}
                />
              </div>
            </div>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-wrap tw-gap-3 tw-items-center tw-mx-7 tw-w-62 tw-mt-5 tw-mb-3">
              {STATUS_FILTER.map((status) => (
                <div key={status.id} className="tw-flex tw-items-center">
                  <RadioButton
                    key={status.id}
                    inputId={status.id}
                    value={status.value}
                    checked={statusFilter === status.value}
                    onChange={(e) =>
                      setStatusFilter(e.value as "all" | "active" | "inactive")
                    }
                  />
                  <label htmlFor={status.id} className="tw-ml-1">
                    {status.label}
                  </label>
                </div>
              ))}
            </div>
          ),
        },
        {
          template: (
            <div className="tw-flex tw-flex-col tw-gap-2 tw-mx-5 tw-w-62 tw-mt-3 tw-mb-4">
              <Button
                className="tw-flex tw-px-3 tw-py-2 tw-rounded tw-bg-red-500 hover:tw-bg-red-500 tw-font-medium tw-gap-2 tw-text-white"
                onClick={clearFilters} // This function will reset filters
              >
                Clear Filters
              </Button>
            </div>
          ),
        },
      ],
    },
  ];

  return (
    <>
      <Toast ref={toast} />
      <div className="card">
        <div className="tw-flex tw-justify-between tw-items-center tw-w-full tw-gap-4 tw-bg-[#F8FAFC] tw-p-4 tw-mr-4">
          <h1 className="tw-font-bold tw-text-xl">Vehicle Listing</h1>
          <div className="tw-flex tw-gap-2 focus:tw-shadow-none">
            <FilterButton menu={menu} />

            <Menu
              className="tw-w-72"
              model={items as MenuItem[]}
              popup
              ref={menu}
            />

            <Button
              className="tw-flex tw-px-3 tw-py-2 tw-rounded bg-primary hover:tw-bg-[#02a2db] border-primary tw-text-white tw-font-medium"
              label="Add Vehicle"
              icon="pi pi-plus"
              onClick={() => {
                setFormType("add");
                setFormValues({
                  registration_no: null,
                  vehicleType: null,
                  capacity: null,
                  isActive: true,
                });
                setIsDialogVisible(true);
              }}
            />
            <Button
              type="button"
              icon="pi pi-upload"
              label="Export"
              loading={isExporting}
              className="tw-flex tw-px-9 tw-py-2 tw-rounded bg-secondary border-primary hover:tw-bg-[#3f3c3c] tw-text-white tw-font-medium focus:tw-shadow-none"
              onClick={() => fetchVehicles(page, size, "export")}
            />
          </div>
        </div>
        <div className="card-body tw-relative tw-min-h-80 table-p-0">
          {vehiclesLoading ? (
            <div className="tw-h-full tw-w-full tw-bg-white tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-0 tw-z-50">
              <ProgressSpinner />
            </div>
          ) : (
            <>
              <DataTableComponent
                rowData={vehicles}
                columns={vehicleColumns}
                customClass="timelog-datatable"
              />

              <Paginator
                first={first}
                rows={size}
                totalRecords={totalRecords} // Set total records for pagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onPageChange={onPageChange}
              />
            </>
          )}
        </div>
      </div>
      <Dialog
        header={formType === "add" ? "Add Vehicle" : "Update Vehicle"}
        visible={isDialogVisible}
        style={{ width: "20vw" }}
        onHide={() => {
          if (!isDialogVisible) return;
          setIsDialogVisible(false);
          setFormErrors((prevFormErrors) => ({
            ...prevFormErrors,
            registration_no: null,
            vehicleType: null,
            capacity: null,
          }));
          setFormValues((prevFormValues) => ({
            ...prevFormValues,
            registration_no: null,
            vehicleType: null,
            capacity: null,
          }));
          setFormType("add");
        }}
      >
        <div className="tw-flex tw-flex-col">
          <div className="tw-mt-0 tw-flex tw-flex-col tw-gap-2">
            <label className="tw-mt-3 tw-font-bold">Vehicle Registration</label>
            <InputText
              value={formValues?.registration_no ?? ""}
              className={`${
                formErrors.registration_no
                  ? "tw-border-red-600"
                  : "tw-border-gray-300"
              } tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal`}
              onChange={(e) => {
                setFormErrors((prevErrors) => ({
                  ...prevErrors,
                  registration_no: null,
                }));
                e.target.value === ""
                  ? setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      registration_no: null,
                    }))
                  : setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      registration_no: e.target.value,
                    }));
              }}
              placeholder="Enter Vehicle Registration"
            />
          </div>
          <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2">
            <label className="tw-mt-3 tw-font-bold">Vehicle Type</label>
            <Dropdown
              value={formValues?.vehicleType ?? null}
              onChange={(e) => {
                setFormErrors((prevErrors) => ({
                  ...prevErrors,
                  vehicleType: null,
                }));
                setFormValues((prevFormValues) => ({
                  ...prevFormValues,
                  vehicleType: e.target.value,
                }));
              }}
              options={vehicleTypes}
              optionLabel="vehicleType"
              placeholder="Select Vehicle Type"
              className={`tw-border tw-border-solid ${
                formErrors.vehicleType
                  ? "tw-border-red-600"
                  : "tw-border-gray-300"
              } tw-border-gray-300 tw-rounded-md placeholder:tw-font-normal tw-w-full`}
            />
          </div>
          <div className="tw-mt-0 tw-flex tw-flex-col tw-gap-2">
            <label className="tw-mt-3 tw-font-bold">Vehicle Make</label>
            <InputText
              value={formValues?.make ?? ""}
              className={`${
                formErrors.make ? "tw-border-red-600" : "tw-border-gray-300"
              } tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal`}
              onChange={(e) => {
                setFormErrors((prevErrors) => ({
                  ...prevErrors,
                  make: null,
                }));
                e.target.value === ""
                  ? setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      make: null,
                    }))
                  : setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      make: e.target.value,
                    }));
              }}
              placeholder="Enter Vehicle Make"
            />
          </div>
          <div className="tw-mt-0 tw-flex tw-flex-col tw-gap-2">
            <label className="tw-mt-3 tw-font-bold">Vehicle Model</label>
            <InputText
              value={formValues?.model ?? ""}
              className={`${
                formErrors.model ? "tw-border-red-600" : "tw-border-gray-300"
              } tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal`}
              onChange={(e) => {
                setFormErrors((prevErrors) => ({
                  ...prevErrors,
                  model: null,
                }));
                e.target.value === ""
                  ? setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      model: null,
                    }))
                  : setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      model: e.target.value,
                    }));
              }}
              placeholder="Enter Vehicle Model"
            />
          </div>
          <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2">
            <label className="tw-mt-3 tw-font-bold">Capacity (In Tons)</label>
            <InputText
              type="number"
              value={formValues?.capacity ?? ""}
              className={`${
                formErrors.capacity ? "tw-border-red-600" : "tw-border-gray-300"
              } tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal`}
              // onKeyDown={ (evt) => (evt.key === 'e' || evt.key === '.') && evt.preventDefault() }
              onKeyDown={(e) => {
                if (
                  e.key === "-" ||
                  e.key === "e" ||
                  e.key === "+" ||
                  e.key === "ArrowUp" ||
                  e.key === "ArrowDown"
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                setFormErrors((prevErrors) => ({
                  ...prevErrors,
                  capacity: null,
                }));
                e.target.value === ""
                  ? setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      capacity: null,
                    }))
                  : setFormValues((prevFormValues) => ({
                      ...prevFormValues,
                      capacity: e.target.value,
                    }));
              }}
              placeholder="Enter Vehicle Capacity (In Tons)"
            />
          </div>
          <Button
            label={formType === "add" ? "Add Vehicle" : "Update Vehicle"}
            className="tw-flex tw-px-3 tw-py-2 tw-rounded-lg bg-primary border-primary tw-text-white tw-font-medium tw-w-full tw-mt-8"
            onClick={() => handleAddUpdateVehicle(formType)}
            disabled={isAddButtonDisabled}
          />
        </div>
      </Dialog>
      <Dialog
        header={`${
          pendingStatusChange?.isActive ? "Activate" : "Deactivate"
        } Vehicle`}
        draggable={false}
        visible={isStatusDialogVisible}
        style={{ width: "28vw" }}
        onHide={() => {
          setIsStatusDialogVisible(false);
          setStatusChangeReason("");
          setPendingStatusChange(null);
        }}
      >
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p>
            Please provide a reason for{" "}
            {pendingStatusChange?.isActive ? "activating" : "deactivating"} this
            vehicle:
          </p>
          <InputText
            value={statusChangeReason}
            onChange={(e) => {
              setStatusChangeReason(e.target.value);
              setStatusChangeReasonError(null);
            }}
            placeholder="Enter reason..."
            className={`tw-px-[14px] tw-py-[5px] tw-border tw-border-solid tw-border-gray-300 tw-rounded-md tw-h-11 placeholder:tw-font-normal ${
              statusChangeReasonError ? "tw-border-red-500" : ""
            }`}
          />
          {statusChangeReasonError && (
            <small className="tw-text-red-500">{statusChangeReasonError}</small>
          )}
          <div className="tw-flex tw-gap-2 tw-justify-end">
            <Button
              label="Cancel"
              className="tw-px-4 tw-py-2 tw-rounded-lg focus:tw-shadow-none tw-bg-gray-300 hover:tw-bg-gray-400 tw-text-gray-800 tw-font-medium"
              onClick={() => {
                setIsStatusDialogVisible(false);
                setStatusChangeReason("");
                setPendingStatusChange(null);
                setStatusChangeReasonError(null);
              }}
            />
            <Button
              label="Confirm"
              className="tw-px-4 tw-py-2 tw-rounded-lg focus:tw-shadow-none bg-primary border-primary hover:tw-bg-[#02a2db] tw-text-white tw-font-medium"
              onClick={confirmStatusChange}
              disabled={isAddButtonDisabled}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        className="comments-dialog tw-w-[444px]"
        style={{ maxWidth: "620px" }}
        header={() => {
          return (
            <div className="tw-flex tw-flex-col tw-justify-start tw-items-start tw-w-full">
              <span>Status Change History</span>
              <span>
                ({selectedRowData?.registration_no} -{" "}
                {selectedRowData?.vehicleType})
              </span>
            </div>
          );
        }}
        visible={viewHistoryPopup}
        draggable={false}
        onHide={() => {
          if (!viewHistoryPopup) return;
          setViewHistoryPopup(false);
        }}
      >
        <>
          {vehicleStatusChangeActivity &&
          vehicleStatusChangeActivity.length !== 0 ? (
            <Timeline
              className="tw-p-4"
              align="left"
              marker={customizedMarker}
              content={customizedContent}
              value={events}
            />
          ) : (
            <div className="tw-p-4 tw-border-b tw-text-center tw-border-b-solid tw-border-b-[#DBDBDB]">
              <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-[#64748B] tw-mb-2">
                No History Found
              </p>
            </div>
          )}
        </>
      </Dialog>
    </>
  );
};

export default VehicleManagement;
